import React, { useState, useEffect, useMemo, useRef } from "react";
import { Layout, Popover, Badge, AutoComplete, Input } from "antd";
import { Link, useHistory } from "react-router-dom";
import { BellOutlined, SearchOutlined } from "@ant-design/icons";
import CustomScrollbars from "../../util/CustomScrollbars";
import languageData from "./languageData";
import {
  switchLanguage,
  toggleCollapsedSideNav,
  setSidebarSearchTarget,
} from "../../appRedux/actions";
import UserInfo from "../../components/UserInfo";
import small from "../../images/small.png";
import {
  BankOutlined,
  UserOutlined,
  SolutionOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_MINI_SIDEBAR,
  TAB_SIZE,
} from "../../constants/ThemeSetting";
import { useDispatch, useSelector } from "react-redux";
import { useScreenWidth } from "../../context/ScreenWidthContext";
import { useAuth } from "../../authentication";
import { useHospitalName } from "../../util/hooks/useHospitalName";
import TopbarNotification from "./NotificationTopBar/TopbarNotification";
import { useSignalRNotifications } from "../../util/api/notification/useSignalRNotifications";
import { buildSidebarSearchIndex } from "../../util/utility-methods";

const { Header } = Layout;

const Topbar = () => {
  const { locale, navStyle } = useSelector(({ settings }) => settings);
  const navCollapsed = useSelector(({ common }) => common.navCollapsed);
  const width = useScreenWidth();
  const [searchText, setSearchText] = useState("");
  const [notificationVisible, setNotificationVisible] = useState(false);
  const dispatch = useDispatch();
  const { authUser, getLogout } = useAuth();
  const { currentUser } = useHospitalName();
  const history = useHistory();

  // const { notifications, unreadCount, isConnected } =
  //   useSignalRNotifications(getLogout);

  // useEffect(() => {
  //   console.log("  - SignalR Connected:", isConnected);
  // }, [notifications, unreadCount, isConnected]);

  const searchIndex = useMemo(
    () => buildSidebarSearchIndex(authUser?.User?.MenuInfo),
    [authUser],
  );

  const [pageSearchValue, setPageSearchValue] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const pageSearchInputRef = useRef(null);

  const pageSearchOptions = useMemo(() => {
    if (!pageSearchValue) return [];
    const lowerSearch = pageSearchValue.toLowerCase();
    return searchIndex
      .filter((item) => item.PName?.toLowerCase().includes(lowerSearch))
      .slice(0, 15)
      .map((item) => ({
        value: item.key,
        label: item.PName,
        item,
      }));
  }, [pageSearchValue, searchIndex]);

  const handlePageSearch = (value) => {
    setPageSearchValue(value);
  };

  const handlePageSelect = (value, option) => {
    const target = option?.item;
    if (!target) return;

    dispatch(
      setSidebarSearchTarget({
        selectedKey: target.selectedKey,
        openKeys: target.openKeys,
        pageURL: target.PageURL,
      }),
    );

    history.push(target.PageURL);
    setPageSearchValue("");
    pageSearchInputRef.current?.blur();
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      const isShortcut =
        (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";
      if (isShortcut) {
        e.preventDefault();
        pageSearchInputRef.current?.focus();
      }
      if (
        e.key === "Escape" &&
        document.activeElement === pageSearchInputRef.current?.input
      ) {
        pageSearchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  const languageMenu = () => (
    <CustomScrollbars className="gx-popover-lang-scroll">
      <ul className="gx-sub-popover">
        {languageData.map((language) => (
          <li
            className="gx-media gx-pointer"
            key={JSON.stringify(language)}
            onClick={() => dispatch(switchLanguage(language))}
          >
            <i className={`flag flag-24 gx-mr-2 flag-${language.icon}`} />
            <span className="gx-language-text">{language.name}</span>
          </li>
        ))}
      </ul>
    </CustomScrollbars>
  );

  return (
    <Header style={{ height: "auto", padding: "0 16px" }}>
      <style>{`
        .topbar-page-search .ant-select-selector,
        .topbar-page-search.ant-input-affix-wrapper {
          height: 34px !important;
          border-radius: 8px !important;
          border: 1px solid #b8bbd1 !important;
          background-color: #ffffff !important;
          box-shadow: none !important;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .topbar-page-search.ant-input-affix-wrapper {
          padding: 4px 11px !important;
        }
        .topbar-page-search .ant-input {
          height: 24px;
          font-size: 13px;
        }
        .topbar-page-search:hover,
        .topbar-page-search.ant-input-affix-wrapper:hover {
          border-color: #05081f !important;
        }
        .topbar-page-search.ant-input-affix-wrapper-focused,
        .topbar-page-search.ant-input-affix-wrapper:focus,
        .topbar-page-search.ant-input-affix-wrapper-focused:hover {
          border-color: #1a2a6c !important;
          box-shadow: 0 0 0 2px rgba(26, 42, 108, 0.12) !important;
        }
        .topbar-page-search .ant-input:focus,
        .topbar-page-search .ant-input-focused {
          box-shadow: none !important;
        }
        /* NEW: Ctrl+K hint badge, jo sirf tab dikhta hai jab field empty + unfocused ho */
        .topbar-search-kbd-hint {
          font-size: 11px;
          color: #9099bb;
          background-color: #f0f2fa;
          border: 1px solid #dde1f2;
          border-radius: 4px;
          padding: 1px 6px;
          line-height: 16px;
          pointer-events: none;
          white-space: nowrap;
        }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          flexWrap: "nowrap",
          minHeight: "65px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            marginRight: "16px",
          }}
        >
          {navStyle === NAV_STYLE_DRAWER ||
          ((navStyle === NAV_STYLE_FIXED ||
            navStyle === NAV_STYLE_MINI_SIDEBAR) &&
            width < TAB_SIZE) ? (
            <div className="gx-linebar gx-mr-3">
              <i
                className="gx-icon-btn icon icon-menu"
                onClick={() => {
                  dispatch(toggleCollapsedSideNav(!navCollapsed));
                }}
              />
            </div>
          ) : null}

          <Link
            className="gx-d-block gx-d-lg-none gx-pointer"
            style={{ display: "flex", alignItems: "center" }}
          >
            <img alt="" src={small} height="28px" />
          </Link>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            marginRight: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "6px",
              flexWrap: "nowrap",
            }}
          >
            <div
              style={{
                fontSize: "19px",
                color: "#1a2a6c",
                fontFamily: "sans-serif",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flexShrink: 0,
              }}
            >
              {authUser?.User?.HsptName || ""}
            </div>

            <AutoComplete
              value={pageSearchValue}
              options={pageSearchOptions}
              onSearch={handlePageSearch}
              onSelect={handlePageSelect}
              onChange={(value) => setPageSearchValue(value)}
              allowClear
              style={{ width: 580, maxWidth: "40vw", marginTop: "3px" }}
              popupMatchSelectWidth={300}
            >
              <Input
                ref={pageSearchInputRef}
                className="topbar-page-search"
                prefix={<SearchOutlined style={{ color: "#9099bb" }} />}
                placeholder="Search pages..."
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                suffix={
                  !isSearchFocused && !pageSearchValue ? (
                    <span className="topbar-search-kbd-hint">Ctrl+K</span>
                  ) : null
                }
              />
            </AutoComplete>
          </div>

          <div
            style={{
              backgroundColor: "#f5f8ff",
              padding: "6px 8px",
              borderRadius: "6px",
              display: "flex",
              gap: "20px",
              color: "#1a2a6c",
              fontSize: "13px",
              flexWrap: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              maxWidth: "fit-content",
            }}
            className="hide-scrollbar"
          >
            <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              <BankOutlined /> Department: {currentUser?.DeptName}
            </span>
            <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              <UserOutlined /> User: {currentUser?.EmpName}
            </span>
            <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              <SolutionOutlined /> Designation: {currentUser?.Designation ?? ""}
            </span>
            <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
              <TeamOutlined /> Role: {currentUser?.RoleName}
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            gap: "16px",
          }}
        >
          {/* <Popover
            content={<TopbarNotification notifications={notifications} />}
            trigger="click"
            placement="bottomRight"
            visible={notificationVisible}
            onVisibleChange={setNotificationVisible}
            overlayStyle={{ paddingTop: "8px" }}
            overlayClassName="professional-notification-popover"
          >
            <Badge
              count={unreadCount}
              offset={[-5, 5]}
              style={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #2f3b99ff 0%, #3747c3ff 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s",
                  border: "3px solid #dadde4ff",
                  boxShadow: "0 4px 12px rgba(41, 52, 142, 0.3)",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.1) rotate(12deg)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(41, 52, 142, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1) rotate(0deg)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(41, 52, 142, 0.3)";
                }}
              >
                {isConnected && (
                  <div
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: "2px",
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: "#52c41a",
                      border: "2px solid white",
                      boxShadow: "0 0 0 2px rgba(82, 196, 26, 0.2)",
                      animation: "pulse 2s ease-in-out infinite",
                    }}
                  />
                )}

                <BellOutlined
                  style={{
                    fontSize: "20px",
                    color: "white",
                    animation:
                      unreadCount > 0
                        ? "bellRing 2s ease-in-out infinite"
                        : "none",
                  }}
                />
              </div>
            </Badge>
          </Popover> */}

          <div className="gx-user-nav">
            <li style={{ listStyle: "none" }}>
              <UserInfo />
            </li>
          </div>
        </div>
      </div>
    </Header>
  );
};

export default Topbar;
